import { addRippleEffect } from './classes';
import '../webapp.css';
import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import logo from '../assets/logo.png';
import { PiList, PiX, PiCaretRightBold } from 'react-icons/pi';

// NoResultBox
function NoResultBox({ title = "No result", description = "You currently have no data" }) {
  return (
    <div className="no-result-box">
      <img src={require('../assets/404.png')} alt="No result" className="no-shifts-img" />
      <h2>{title}</h2>
      <p className="small-text">{description}</p>
    </div>
  );
}

// ConfirmationModal
function ConfirmationModal({
  open, // boolean
  title = 'Are you sure?',
  message = '',
  confirmText = 'Submit',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isSubmitting
}) {
  if (!open) return null;

  const modal = (
    <div className="modal-overlay">
      <div className="popupbox-content">
        <div className="popupbox-message">
          {title}
          <p>{message}</p>
        </div>
        <div className="popupbox-actions">
          <button
            className="popupbox-btn popupbox-btn-cancel"
            onClick={e => { addRippleEffect(e); onCancel(); }}
            disabled={isSubmitting}
          >{cancelText}</button>
          <button
            className="popupbox-btn popupbox-btn-confirm"
            onClick={e => { addRippleEffect(e); onConfirm(); }}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Spinner /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(modal, document.body);
}

// AlertBox component with dynamic bg and text color, and swoosh-in animation
/**
 * @param {Object} props
 * @param {string} props.message - The alert message
 * @param {string} [props.type] - 'success' | 'error'
 * @param {function} [props.onClose] - Optional close handler
 */
function AlertBox({ message, type, onClose }) {
  const [closing, setClosing] = useState(false);
  const boxRef = useRef(null);

  // Auto-close after 7 seconds
  useEffect(() => {
    if (!closing) {
      const timer = setTimeout(() => setClosing(true), 7000);
      return () => clearTimeout(timer);
    }
  }, [closing]);

  useEffect(() => {
    if (closing && boxRef.current) {
      const handleAnimEnd = () => {
        if (onClose) onClose();
      };
      const node = boxRef.current;
      node.addEventListener('animationend', handleAnimEnd);
      return () => node.removeEventListener('animationend', handleAnimEnd);
    }
  }, [closing, onClose]);

  let bg = '';
  let color = '#fff';
  if (type === 'success') {
    bg = '#298635';
  } else if (type === 'error') {
    bg = '#EF4444';
  }

  return (
    <div
      ref={boxRef}
      className={`alertbox ${closing ? 'alertbox-out' : 'alertbox-in'}`}
      style={{ background: bg, color }}
    >
      <span>{message}</span>
    </div>
  );
}

// AlertStack: renders multiple AlertBoxes stacked, newest on top
function AlertStack({ alerts, onAlertClose }) {
  const alertHeight = 43;
  return (
    <div style={{ position: 'fixed', top: 0, left: '50%'}}>
      {[...alerts].reverse().map((alert, idx) => {
        const offset = idx * alertHeight;
        return (
          <div
            key={alert.id}
            style={{
              position: 'absolute',
              transform: `translate(-50%, 0)`,
              top: offset,
              transition: idx === 0 ? 'none' : 'top 0.35s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            <AlertBox
              message={alert.message}
              type={alert.type}
              onClose={() => onAlertClose(alert.id)}
            />
          </div>
        );
      })}
    </div>
  );
}

// Navbar Component
function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleGotoAccount = () => {
    setMenuOpen(false);
    navigate('/login');
  };

  // Handle scroll effect on Navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu on route change (optional, for SPA UX)
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [menuOpen]);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="logo-container">
          <img src={logo} alt="Logo" className="navbar-logo" />
        </div>
        <button
          className="navbar-menu-toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? <PiX size={28} /> : <PiList size={28} />}
        </button>
        <div className={`mini-button-container navbar-menu${menuOpen ? ' open' : ''}`}>
          <button className="mini-button mini-button-nav" onClick={handleGotoAccount}>
            <p>Go to account</p> <PiCaretRightBold/>
          </button>
        </div>
      </div>
    </nav>
  );
}

// Navbar2: Existing code for Navbar2
const Navbar2 = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = () => {
    setIsScrolled(window.scrollY > 50); // Add shadow when scrolled more than 50px
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav className={`navbar2 ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar2-container">
        <img src={logo} alt="Logo" className="navbar-logo" onClick={addRippleEffect} />
      </div>
    </nav>
  );
};

// Spinner component
function Spinner({ color = '#fff', size = 20 }) {
  return (
    <span
      className="spinner"
      style={{
        width: size,
        height: size,
        borderColor: color,
        borderTopColor: 'transparent',
      }}
    />
  );
}

export { Navbar, NoResultBox, ConfirmationModal, AlertStack, Navbar2, Spinner };

